import { useEffect, useState, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { CustomerView } from "@/components/CustomerView";
import { StaffHome } from "@/components/StaffView";
import { OwnerDashboard } from "@/components/OwnerDashboard";
import { OnboardingWizard } from "@/components/OnboardingWizard";
import { AdminConsole } from "@/components/AdminConsole";
import { JoinScreen } from "@/components/JoinScreen";
import { CollectScreen } from "@/components/CollectScreen";
import { Login } from "@/components/Login";
import { Register } from "@/components/Register";
import { QrPoster } from "@/components/QrPoster";
import { FeedbackScreen } from "@/components/FeedbackScreen";
import { MenuScreen } from "@/components/MenuScreen";
import { BusinessLayout, Loading } from "@/components/Layout";
import { AuthProvider, useAuth } from "@/AuthContext";
import { getMyState } from "@/api";

function CustomerContainer() {
  const { logout } = useAuth();
  const [state, setState] = useState(null);
  const load = useCallback(async () => {
    const tokenAtStart = localStorage.getItem("bs_token");
    try {
      const s = await getMyState();
      // Ignore a late response if the active business/token changed mid-flight
      // (e.g. rapid A→B switch) so B never briefly renders A's data.
      if (localStorage.getItem("bs_token") !== tokenAtStart) return;
      setState(s);
    } catch (e) { /* handled by auth interceptor */ }
  }, []);
  useEffect(() => {
    load();
    const iv = setInterval(load, 8000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, [load]);
  if (!state) return (
    <div className="max-w-md mx-auto min-h-screen bg-slate-50"><Loading /></div>
  );
  return (
    <BusinessLayout business={state.business} compact>
      <CustomerView state={state} onRefresh={load} />
    </BusinessLayout>
  );
}

function HomeRoute() {
  const { user } = useAuth();
  if (!user) {
    // Keep a logged-out customer inside their business context instead of the
    // platform login, so logout never dumps them on the admin/staff sign-in.
    const slug = localStorage.getItem("bs_slug");
    return <Navigate to={slug ? `/join/${slug}?loggedout=1` : "/login"} replace />;
  }
  if (user.role === "platform_admin") return <AdminConsole />;
  if (user.role === "owner") return user.business_id ? <OwnerDashboard /> : <OnboardingWizard />;
  if (user.role === "staff") return <StaffHome />;
  return <CustomerContainer />;
}

function SupportRoute() {
  const { user } = useAuth();
  const bid = localStorage.getItem("bs_support_bid");
  if (!user || user.role !== "platform_admin" || !bid) return <Navigate to="/" replace />;
  return <OwnerDashboard supportMode />;
}

function JoinRoute() {
  // Business QR entry. The scanned :slug is the single source of truth for which
  // business to show. JoinScreen resolves & validates that business first, then
  // opens its remembered card only if it belongs to THIS business — never
  // redirecting to a different, last-used business's card.
  return <JoinScreen />;
}

function AuthRoute({ children }) {
  const { user } = useAuth();
  if (user && user.role !== "customer") return <Navigate to="/" replace />;
  return children;
}

function Shell() {
  const { loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loading /></div>;
  return (
    <>
      <Routes>
        <Route path="/login" element={<AuthRoute><Login /></AuthRoute>} />
        <Route path="/register" element={<AuthRoute><Register /></AuthRoute>} />
        <Route path="/join/:slug/collect" element={<CollectScreen />} />
        <Route path="/join/:slug" element={<JoinRoute />} />
        <Route path="/support" element={<SupportRoute />} />
        <Route path="/" element={<HomeRoute />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster position="bottom-center" richColors />
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/poster/:slug" element={<QrPoster />} />
          <Route path="/feedback/:slug" element={<FeedbackScreen />} />
          <Route path="/menu/:slug" element={<MenuScreen />} />
          <Route path="*" element={<Shell />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
